import { useEffect, useState } from "react";
import { http } from "@/lib/api";

const dur = (s) => (s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m ${String(Math.round(s % 60)).padStart(2, "0")}s`);
const Kpi = ({ label, value, testid }) => (
  <div className="border border-hv-rule p-4" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-hv-muted">{label}</p>
    <p className="mt-1 font-heading text-2xl font-bold tabular-nums text-hv-navy">{value}</p>
  </div>
);

const Buckets = ({ buckets, total }) => (
  <div className="space-y-2" data-testid="engagement-question-buckets">
    {buckets.map((b) => (
      <div key={b.label} className="flex items-center gap-3 text-[12px]">
        <span className="w-28 text-hv-muted">{b.label}</span>
        <div className="h-2 flex-1 bg-hv-section"><div className="h-full bg-hv-logo-navy" style={{ width: `${total ? (b.sessions / total) * 100 : 0}%` }} /></div>
        <span className="w-10 text-right font-semibold tabular-nums">{b.sessions}</span>
      </div>
    ))}
  </div>
);

const TopDocs = ({ docs }) => (
  <table className="w-full text-left text-[13px]" data-testid="engagement-top-docs">
    <thead><tr className="border-b border-hv-rule text-[11px] uppercase tracking-wider text-hv-muted">
      <th className="py-2 pr-3 font-heading font-semibold">Document</th><th className="py-2 pr-3 font-heading font-semibold">Opens</th><th className="py-2 font-heading font-semibold">Sessions</th>
    </tr></thead>
    <tbody>
      {docs.length === 0 && <tr><td colSpan={3} className="py-3 text-hv-muted">No documents opened from Ceres yet.</td></tr>}
      {docs.map((d) => (
        <tr key={d.url} className="border-b border-hv-rule/60">
          <td className="max-w-[360px] truncate py-2 pr-3"><a href={d.url} target="_blank" rel="noreferrer" className="hover:underline" title={d.url}>{d.label}</a></td>
          <td className="py-2 pr-3 tabular-nums">{d.opens}</td><td className="py-2 tabular-nums">{d.sessions}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Daily = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-[13px]" data-testid="engagement-daily-table">
      <thead><tr className="border-b border-hv-rule text-[11px] uppercase tracking-wider text-hv-muted">
        {["Day", "Sessions", "Avg length", "Avg questions", "PDF opens", "Advisor Briefs"].map((h) => <th key={h} className="py-2 pr-3 font-heading font-semibold">{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.date} className="border-b border-hv-rule/60 tabular-nums">
            <td className="py-2 pr-3">{r.date}</td><td className="py-2 pr-3">{r.sessions}</td><td className="py-2 pr-3">{dur(r.avg_seconds)}</td>
            <td className="py-2 pr-3">{r.avg_questions}</td><td className="py-2 pr-3">{r.doc_opens}</td><td className="py-2 pr-3">{r.briefs}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const EngagementPanel = ({ days }) => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/admin/engagement?days=${days}`).then((r) => setD(r.data)); }, [days]);
  if (!d) return null;
  const s = d.sessions;
  return (
    <section className="border border-hv-rule bg-white p-6" data-testid="panel-engagement">
      <h2 className="border-b border-hv-rule pb-3 font-heading text-[14px] font-semibold text-hv-text">
        Sessions and PDF opens <span className="font-normal text-hv-muted">· last {d.days} days · sessions with at least one question{s.tracked_since ? ` · window timing since ${s.tracked_since}` : ""}</span>
      </h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Sessions" value={s.sessions.toLocaleString("en-CA")} testid="engagement-kpi-sessions" />
        <Kpi label="Avg session length" value={dur(s.avg_seconds)} testid="engagement-kpi-avg-length" />
        <Kpi label="Median length" value={dur(s.median_seconds)} testid="engagement-kpi-median-length" />
        <Kpi label="Questions per session" value={s.avg_questions} testid="engagement-kpi-avg-questions" />
        <Kpi label="Harvest PDFs opened" value={d.documents.opens.toLocaleString("en-CA")} testid="engagement-kpi-doc-opens" />
        <Kpi label="Advisor Briefs" value={`${d.briefs.downloads}`} testid="engagement-kpi-briefs" />
      </div>
      <div className="mt-6 grid gap-8 lg:grid-cols-2">
        <div><p className="mb-3 text-[12px] font-semibold text-hv-text">Questions per session</p><Buckets buckets={s.buckets} total={s.sessions} /></div>
        <div><p className="mb-2 text-[12px] font-semibold text-hv-text">Most opened documents</p><TopDocs docs={d.documents.top} /></div>
      </div>
      <p className="mb-2 mt-6 text-[12px] font-semibold text-hv-text">By day</p>
      <Daily rows={d.daily} />
      <p className="mt-2 text-[11px] text-hv-muted">Advisor Briefs: {d.briefs.personalized} of {d.briefs.downloads} included the advisor's own name and firm.</p>
    </section>
  );
};
